import { X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../../../../../../utils/UserContext';

export const ActivateNewCardModal = ({
  isOpen,
  onClose,
  debit_card_id,
}: {
  isOpen: boolean;
  onClose: () => void;
  debit_card_id: string;
}) => {
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const { userId } = useUser();
  const queryClient = useQueryClient();

  const handleActivatePhysicalCard = async () => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/debitCards/${debit_card_id}/activatePhysicalCard`,
        'POST',
        {},
        'Error occurred while activating physical card.'
      );

      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Successfully activated physical card.');
        await queryClient.invalidateQueries({
          queryKey: ['debitCards', userId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['fetchCards', userId],
        });
      } else {
        toast.error('Error occurred while activating physical card.');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating newly linked account record: ',
        e
      );
    } finally {
      onClose();
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-gray-900 bg-opacity-50"
            onClick={loading ? undefined : onClose}
          >
            <div
              className="h-auto max-h-[90%] w-full max-w-[90%] overflow-x-hidden rounded-[4px] bg-white md:max-w-[400px]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-[56px] w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Activate Physical Card
                </h2>
                <X
                  className={
                    loading
                      ? 'pointer-events-none opacity-40'
                      : 'hover:cursor-pointer hover:opacity-80'
                  }
                  onClick={loading ? undefined : onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              <div>
                <p className="p-4 text-base text-graysecondary">
                  Activating your physical card means it can be swiped in
                  person. Please only activate your card once you have received
                  it in the mail.
                </p>
              </div>

              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 border-t-[1px] border-bordergray p-4">
                <NeutralButton
                  onClick={onClose}
                  disabled={loading}
                  addClass="rounded-full w-[178px] h-[48px] flex flex-row items-center justify-center"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  disabled={loading}
                  onClick={async () => await handleActivatePhysicalCard()}
                  addClass="rounded-full hover:bg-yellow-600 w-[178px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600 flex flex-row items-center justify-center"
                >
                  {!loading ? (
                    'Activate'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default ActivateNewCardModal;
